import { Dna, Library, Radar, ClipboardCheck, MessageCircle, Send } from 'lucide-react';
import type { SetupSignals, SetupStepDefinition, SetupStepId } from '@/types';

/**
 * Every step id in display order. Order matters — it drives the next-step
 * selection and the order the checklist reads in, so the list runs top to
 * bottom as the intended path.
 */
export const SETUP_STEP_IDS: readonly SetupStepId[] = [
  'brand-dna',
  'knowledge-base',
  'competitors',
  'review-queue',
  'chat',
  // Last, and flagged `optional` in SETUP_STEPS: linking Telegram is an extra,
  // not a prerequisite, so it neither counts towards setup nor becomes the
  // next step.
  'telegram',
];

export const SETUP_STEPS: readonly SetupStepDefinition[] = [
  {
    id: 'brand-dna',
    label: 'Brand DNA',
    shortLabel: 'DNA',
    todo: 'Fill in identity, goals, topics and voice',
    done: 'Your Brand DNA is complete',
    icon: Dna,
    route: '/app/preferences',
  },
  {
    id: 'knowledge-base',
    label: 'Knowledge Base',
    shortLabel: 'KB',
    todo: 'Add a document, link or note for LiLi to learn from',
    done: 'LiLi has indexed your knowledge base',
    icon: Library,
    route: '/app/knowledge-base',
  },
  {
    id: 'competitors',
    // Labelled to match the sidebar entry for the page it opens, so the step and
    // its destination can't read as two different features.
    label: 'Social Listening',
    shortLabel: 'Listening',
    todo: 'Track at least one competitor account',
    done: 'You are tracking competitors',
    icon: Radar,
    route: '/app/topic-trends',
    alsoAffectedBy: ['/app/competitors'],
  },
  {
    id: 'review-queue',
    label: 'Review Queue',
    shortLabel: 'Review',
    todo: 'Approve or reject your first generated post',
    done: 'You have reviewed your first post',
    icon: ClipboardCheck,
    route: '/app/content-review',
  },
  {
    id: 'chat',
    label: 'Chat with LiLi',
    shortLabel: 'Chat',
    todo: 'Start your first conversation with LiLi',
    done: 'You have chatted with LiLi',
    icon: MessageCircle,
    route: '/app/chat',
  },
  {
    id: 'telegram',
    label: 'Connect Telegram',
    shortLabel: 'Telegram',
    todo: 'Link Telegram to reach LiLi from your phone',
    done: 'LiLi is connected to your Telegram',
    // lucide has no Telegram glyph; the paper plane is the closest match and is
    // already the visual idiom for the app.
    icon: Send,
    // The Channels page hosts the full linking card.
    route: '/app/channels',
    // Offered, never required. Counting it would un-complete every account that
    // had already reached 100% before Telegram existed.
    optional: true,
    // Off until the Module 2 routes are on the backend. While off the row is
    // absent and GET /telegram/status is never called.
    flag: 'telegram-linking',
  },
];

export const EMPTY_SETUP_SIGNALS: SetupSignals = {
  brandDnaPercent: null,
  knowledgeBaseIndexedCount: null,
  competitorCount: null,
  reviewedPostCount: null,
  chatConversationCount: null,
  telegramLinked: null,
};

/** Which signal field backs each step — used to map a refresh onto a step. */
export const SIGNAL_KEY_BY_STEP: Record<SetupStepId, keyof SetupSignals> = {
  'brand-dna': 'brandDnaPercent',
  'knowledge-base': 'knowledgeBaseIndexedCount',
  competitors: 'competitorCount',
  'review-queue': 'reviewedPostCount',
  chat: 'chatConversationCount',
  telegram: 'telegramLinked',
};
